import { 
  Clock, 
  CreditCard, 
  MapPin, 
  Shield, 
  Users, 
  Calendar, 
  Wallet, 
  TrendingUp,
  CheckCircle2
} from 'lucide-react';

const producerBenefits = [
  {
    icon: Clock,
    title: 'Disponibilidad inmediata',
    description: 'Encuentra maquinaria y servicios disponibles al instante, sin largas esperas.'
  },
  {
    icon: CreditCard,
    title: 'Pago en cuotas',
    description: 'Financia tus trabajos agrícolas en cuotas flexibles con bancos aliados.'
  },
  {
    icon: MapPin,
    title: 'Prestadores cercanos',
    description: 'Localiza los mejores prestadores en tu zona con geolocalización.'
  },
  {
    icon: Shield,
    title: 'Garantía de servicio',
    description: 'Trabaja con prestadores verificados y calificados por otros productores.'
  }
];

const providerBenefits = [
  {
    icon: Users,
    title: 'Nueva clientela',
    description: 'Accede a miles de productores buscando tus servicios activamente.'
  },
  {
    icon: Calendar,
    title: 'Agenda organizada',
    description: 'Gestiona todos tus trabajos desde una sola plataforma intuitiva.'
  },
  {
    icon: Wallet,
    title: 'Cobro garantizado',
    description: 'Recibe el pago de forma segura e inmediata al completar el servicio.'
  },
  {
    icon: TrendingUp,
    title: 'Crece tu negocio',
    description: 'Aumenta tu visibilidad y reputación con cada trabajo bien hecho.'
  }
];

const faqs = [
  {
    question: '¿Cómo me registro en Prestagro?',
    answer: 'Descarga la app, ingresa tu email o teléfono, completa tu perfil y comienza a usar la plataforma en minutos.'
  },
  {
    question: '¿Qué comisión cobra Prestagro?',
    answer: 'El plan básico tiene una comisión del 10% por servicio. Con el plan Profesional, la comisión baja al 5%.'
  },
  {
    question: '¿Cómo se garantiza el pago?',
    answer: 'Los fondos se retienen hasta que el servicio se completa y ambas partes confirman. Luego se libera automáticamente.'
  }
];

export const BenefitsSection = () => {
  return (
    <section
      id="beneficios"
      className="py-16 md:py-24 bg-[#FBF5EA]"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-bold uppercase tracking-[0.2em] text-[#AB6D4C] mb-4">
            Beneficios
          </span>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#051914] mb-6">
            Para cada tipo de usuario
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-[#051914]/70 max-w-3xl mx-auto">
            Prestagro está diseñado para resolver los problemas específicos de 
            productores y prestadores rurales.
          </p>
        </div>

        {/* Two Column Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {/* Producers Card */}
          <div 
            className="bg-[#A9CB68] rounded-3xl p-8 md:p-10"
            data-testid="benefits-productores"
          >
            <div className="mb-8">
              <span className="inline-block px-4 py-1 bg-[#051914] text-[#FBF5EA] rounded-full text-sm font-medium mb-4">
                Para Productores
              </span>
              <h3 className="text-2xl md:text-3xl font-medium text-[#051914] mb-3">
                Optimiza tu producción
              </h3>
              <p className="text-[#051914]/70">
                Accede a los mejores servicios agropecuarios sin complicaciones.
              </p>
            </div>

            <div className="space-y-6">
              {producerBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#051914] rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon size={24} className="text-[#A9CB68]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#051914] mb-1">
                      {benefit.title}
                    </h4>
                    <p className="text-sm text-[#051914]/70">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Providers Card */}
          <div 
            className="bg-[#051914] rounded-3xl p-8 md:p-10"
            data-testid="benefits-prestadores"
          >
            <div className="mb-8">
              <span className="inline-block px-4 py-1 bg-[#A9CB68] text-[#051914] rounded-full text-sm font-medium mb-4">
                Para Prestadores
              </span>
              <h3 className="text-2xl md:text-3xl font-medium text-[#FBF5EA] mb-3">
                Hace crecer tu negocio
              </h3>
              <p className="text-[#FBF5EA]/70">
                Encontrá nuevos clientes y organizá tu trabajo de manera eficiente.
              </p>
            </div>

            <div className="space-y-6">
              {providerBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#A9CB68] rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon size={24} className="text-[#051914]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#FBF5EA] mb-1">
                      {benefit.title}
                    </h4>
                    <p className="text-sm text-[#FBF5EA]/70">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-[#EBE9BD] rounded-3xl p-8 md:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-medium text-[#051914] mb-3">
              Preguntas frecuentes
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-[#FBF5EA] rounded-2xl p-6"
                data-testid={`faq-item-${index}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle2 size={20} className="text-[#A9CB68] flex-shrink-0 mt-0.5" />
                  <h4 className="font-medium text-[#051914]">{faq.question}</h4>
                </div>
                <p className="text-sm text-[#051914]/60 ml-8">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
